import { appelerFonction, supabase } from './supabase'
import { nomLisible, type ContactValide } from './contactCompte'
import { corpsCreationLivreur, corpsCreationMarchand, corpsInvitationMembre } from './corpsCreation'
import type { ReponseCreation } from './identifiants'
import { toutesLesLignes } from './pages'
import {
  type Client,
  type Commande,
  type Configuration,
  type FilsParPair,
  type Livreur,
  type Marchand,
  type MembreStaff,
  type PaieLivreur,
  type Paiement,
  type PeriodePayee,
  type Remboursement,
  type StatutCommande,
  etatLivreur,
} from './types'
import { APERCU, apercuConfig, apercuAppliquer, apercuLivreurs, apercuAnnuler, apercuAffecter } from './apercu'
import { noteAutoDepuisBase, validerNoteAuto } from './notes'
import { kindDepuisSaisie } from './typeMarchand'
import { positionConnue, sansLeClient, trierParDistance, type Point } from './livreursAssignables'
import {
  estConditionMeteo,
  validerFraisService,
  validerMinima,
  validerPaliers,
  type ConditionMeteo,
  type FraisServiceASaisir,
  type MinimaASaisir,
  type PalierASaisir,
} from './tarifs'
import {
  COLONNES_CHAT,
  estEchangeClientLivreur,
  lireCleFil,
  regrouperDiscussions,
  regrouperFils,
  type DiscussionCommande,
  type InfosCommande,
  type LigneChat,
} from './discussions'

/**
 * Toutes les opérations de données de la console Dispatch.
 *
 * Aucune règle métier n'est rejouée ici : totaux, commissions, transitions de
 * statut et calculs de paie restent au serveur (edge functions et RPC). Une
 * seconde implémentation côté navigateur finirait par diverger — et l'écart se
 * découvrirait sur une paie ou un reversement.
 */

/** Postgres renvoie « 12:00:00 » ; les champs de saisie travaillent en hh:mm. */
const hhmm = (t: string | null): string => String(t ?? '').slice(0, 5)

// ── Livreurs ─────────────────────────────────────────────────────────────

interface LigneLivreur {
  id: string
  vehicle_type: string | null
  vehicle_plate: string | null
  vehicle_model: string | null
  payout_provider: string | null
  payout_phone: string | null
  status: string | null
  rating: number | null
  total_deliveries: number | null
  is_verified: boolean
  created_at: string
  lat: number | null
  lng: number | null
  profile: { full_name: string | null; phone: string | null } | null
}

/** Une ligne de la vue `livreur_notes` (migration 0048). */
interface LigneNote {
  driver_id: string
  avis: number
  sans_avis: number
  livraisons: number
  note: number | string | null
}

/**
 * Les notes mixtes, par livreur. Si la migration 0048 n'est pas encore en ligne la
 * vue n'existe pas : on rend une table vide plutôt que de faire échouer l'écran —
 * les livreurs s'affichent alors avec leur note brute et SANS faux « 0 avis ».
 */
async function lireNotesLivreurs(): Promise<Map<string, LigneNote>> {
  const { data, error } = await supabase
    .from('livreur_notes')
    .select('driver_id,avis,sans_avis,livraisons,note')
  if (error || !data) return new Map()
  return new Map((data as unknown as LigneNote[]).map((n) => [n.driver_id, n]))
}

export async function listerLivreurs(): Promise<Livreur[]> {
  if (APERCU) return structuredClone(apercuLivreurs())
  const [{ data, error }, notes] = await Promise.all([
    supabase.from('drivers').select('*, profile:profiles!id(full_name,phone)'),
    lireNotesLivreurs(),
  ])
  if (error) throw error
  return ((data as unknown as LigneLivreur[] | null) ?? []).map((d) => {
    const n = notes.get(d.id)
    return {
      id: d.id,
      nom: d.profile?.full_name ?? 'Livreur',
      telephone: d.profile?.phone ?? d.payout_phone ?? '',
      typeVehicule: d.vehicle_type,
      plaque: d.vehicle_plate ?? '—',
      modele: d.vehicle_model ?? '',
      fournisseurPaie: d.payout_provider === 'orange_money' ? 'om' : 'wave',
      numeroPaie: d.payout_phone ?? '',
      etat: etatLivreur(d.status),
      note: d.rating,
      noteMixte: n && n.note !== null ? Number(n.note) : null,
      avis: n ? n.avis : null,
      sansAvis: n ? n.sans_avis : null,
      // `drivers.total_deliveries` n'est écrit par personne : le compte réel vient de la vue.
      livraisons: n ? n.livraisons : (d.total_deliveries ?? 0),
      verifie: d.is_verified,
      creeLe: d.created_at,
      lat: d.lat ?? 0,
      lng: d.lng ?? 0,
    }
  })
}

// ── Marchands ────────────────────────────────────────────────────────────

interface LigneMarchand {
  id: string
  name: string
  kind: string | null
  quartier: string | null
  is_open_now: boolean
  blocked_reason: string | null
  paused_until: string | null
  rating: number | null
  phone: string | null
  description: string | null
  created_at: string
  lat: number | null
  lng: number | null
  category_ids: number[] | null
  owner_id: string | null
  gerant: { email: string | null; full_name: string | null; phone: string | null } | null
}

export async function listerMarchands(): Promise<Marchand[]> {
  const { data, error } = await supabase
    .from('merchants')
    .select('*,is_open_now, gerant:profiles!merchants_owner_id_fkey(email,full_name,phone)')
  if (error) throw error
  return ((data as unknown as LigneMarchand[] | null) ?? []).map((m) => ({
    id: m.id,
    nom: m.name,
    type: m.kind === 'supermarket' ? 'supermarche' : 'restaurant',
    quartier: m.quartier ?? '',
    // État CALCULÉ par la base (migration 0028) : horaires du jour, pause,
    // blocage. La console ne le recalcule pas — elle se tromperait.
    etat: m.blocked_reason ? 'bloque' : m.is_open_now ? 'ouvert' : 'ferme',
    motifBlocage: m.blocked_reason,
    pauseJusqua: m.paused_until,
    note: m.rating,
    telephone: m.phone ?? '',
    description: m.description ?? '',
    creeLe: m.created_at,
    lat: m.lat ?? 0,
    lng: m.lng ?? 0,
    positionConnue: m.lat != null && m.lng != null,
    categories: m.category_ids ?? [],
    email: m.gerant?.email ?? '',
    telephoneGerant: m.gerant?.phone ?? '',
    nomContact: m.gerant?.full_name ?? '',
    proprietaireId: m.owner_id,
  }))
}

export interface Rubrique {
  id: number
  nom: string
}

/**
 * Les rubriques de l'app client (Malien, Grillades…). « Tout » est écartée :
 * c'est le bouton qui RETIRE le filtre côté app, aucun marchand ne doit la
 * porter.
 */
export async function listerRubriques(): Promise<Rubrique[]> {
  const { data, error } = await supabase
    .from('merchant_categories')
    .select('id,name,sort_order')
    .order('sort_order')
  if (error) throw error
  return ((data as { id: number; name: string }[] | null) ?? [])
    .filter((r) => r.name !== 'Tout')
    .map((r) => ({ id: r.id, nom: r.name }))
}

/**
 * Ce que le DISPATCH règle sur la fiche d'un établissement : ses rubriques et
 * son point de retrait. Le classement dans le catalogue est une décision de
 * SwiftEats, pas du marchand ; la position conditionne les frais de livraison.
 */
export async function modifierFicheMarchand(
  id: string,
  patch: { categories?: number[]; lat?: number; lng?: number },
): Promise<void> {
  const ligne: Record<string, unknown> = {}
  if (patch.categories) ligne.category_ids = patch.categories
  if (patch.lat != null && patch.lng != null) {
    ligne.lat = patch.lat
    ligne.lng = patch.lng
  }
  const { error } = await supabase.from('merchants').update(ligne).eq('id', id)
  if (error) throw error
}

export interface CommissionArticle {
  id: string
  nom: string
  /** Prix du MARCHAND (son net, 0055). */
  prix: number
  /** Prix client calculé par la base (0055) ; `null` = base pas encore à jour. */
  prixClient: number | null
  taux: number
  disponible: boolean
  section: string
}

/**
 * Les articles d'un marchand avec leur taux (0055 : MODIFIABLE par le dispatch seul ;
 * le marchand ne voit plus de champ de taux) et leurs deux prix.
 */
export async function listerCommissions(marchandId: string): Promise<CommissionArticle[]> {
  const lire = (cols: string) =>
    supabase
      .from('menu_items')
      .select(cols + ',section:menu_sections!section_id(name)')
      .eq('merchant_id', marchandId)
      .order('name')
  let r = await lire('id,name,price,prix_client,commission_pct,is_available')
  // Base sans 0055 : pas de `prix_client` — on relit sans (le prix saisi est alors encore le prix client).
  if (r.error && /prix_client/.test(r.error.message) && (r.error.code === '42703' || /does not exist|could not find/i.test(r.error.message)))
    r = await lire('id,name,price,commission_pct,is_available')
  if (r.error) throw r.error
  type Ligne = {
    id: string
    name: string
    price: number
    prix_client?: number | null
    commission_pct: number | null
    is_available: boolean
    section: { name: string | null } | null
  }
  return ((r.data as unknown as Ligne[] | null) ?? []).map((i) => ({
    id: i.id,
    nom: i.name,
    prix: i.price,
    prixClient: typeof i.prix_client === 'number' ? i.prix_client : null,
    taux: Number(i.commission_pct ?? 0),
    disponible: i.is_available,
    section: i.section?.name ?? '',
  }))
}

/** Réglages du marchand décidés par le dispatch (0055). `null` = colonne absente (base pas à jour). */
export interface ReglagesMarchand {
  /** Taux contractuel, en fraction. */
  taux: number | null
  /** Minimum de commande propre au restaurant ; `null` = minimum plateforme. */
  minimumCommande: number | null
}

export async function lireReglagesMarchand(marchandId: string): Promise<ReglagesMarchand> {
  const { data, error } = await supabase.from('merchants').select('*').eq('id', marchandId).single()
  if (error) throw error
  const m = data as { commission_pct?: number | string | null; min_order?: number | null }
  return {
    taux: m.commission_pct == null ? null : Number(m.commission_pct),
    minimumCommande: typeof m.min_order === 'number' ? m.min_order : null,
  }
}

/** Écrit le taux contractuel et le minimum propre. Vérifie qu'une ligne a bien été modifiée. */
export async function enregistrerReglagesMarchand(marchandId: string, r: { taux: number; minimumCommande: number | null }): Promise<void> {
  const { data, error } = await supabase
    .from('merchants')
    .update({ commission_pct: r.taux, min_order: r.minimumCommande })
    .eq('id', marchandId)
    .select('id')
  if (error) throw new Error(error.message)
  if (!data?.length) throw new Error('Modification refusée (droits insuffisants).')
}

/** Taux d'UN article (le prix client et ceux de ses suppléments suivent, calculés par la base). */
export async function modifierTauxArticle(articleId: string, taux: number): Promise<void> {
  const { data, error } = await supabase.from('menu_items').update({ commission_pct: taux }).eq('id', articleId).select('id')
  if (error) throw new Error(error.message)
  if (!data?.length) throw new Error('Modification refusée (droits insuffisants : permission « gérer les marchands »).')
}

/** « Appliquer ce taux à tous les articles de ce marchand » : une seule écriture. Rend le nombre d'articles. */
export async function appliquerTauxATousLesArticles(marchandId: string, taux: number): Promise<number> {
  const { data, error } = await supabase
    .from('menu_items')
    .update({ commission_pct: taux })
    .eq('merchant_id', marchandId)
    .select('id')
  if (error) throw new Error(error.message)
  return data?.length ?? 0
}

export interface ResumeCommission {
  nb: number
  base: number
  moyenne: number
  min: number
  max: number
}

/** Résumé pour TOUS les marchands en une requête — la liste en affiche neuf. */
export async function resumeCommissions(): Promise<Record<string, ResumeCommission>> {
  const { data, error } = await supabase
    .from('menu_items')
    .select('merchant_id,price,commission_pct')
  if (error) throw error

  const brut: Record<string, { nb: number; base: number; pond: number; min: number; max: number }> = {}
  for (const i of (data ?? []) as Array<{
    merchant_id: string
    price: number | null
    commission_pct: number | null
  }>) {
    const a = (brut[i.merchant_id] ??= { nb: 0, base: 0, pond: 0, min: Infinity, max: -Infinity })
    const taux = Number(i.commission_pct ?? 0)
    const prix = Number(i.price ?? 0)
    a.nb++
    a.base += prix
    // Moyenne PONDÉRÉE par le prix, pas moyenne des taux : un plat à 500 F et
    // un à 15 000 F ne pèsent pas pareil dans ce qu'on facture réellement.
    a.pond += prix * taux
    if (taux < a.min) a.min = taux
    if (taux > a.max) a.max = taux
  }

  const resume: Record<string, ResumeCommission> = {}
  for (const [id, a] of Object.entries(brut)) {
    resume[id] = {
      nb: a.nb,
      base: a.base,
      moyenne: a.base > 0 ? a.pond / a.base : 0,
      min: Number.isFinite(a.min) ? a.min : 0,
      max: Number.isFinite(a.max) ? a.max : 0,
    }
  }
  return resume
}

// ── Clients ──────────────────────────────────────────────────────────────

/**
 * Les clients de la plateforme — vue `clients_plateforme` (migration 0053).
 *
 * ⚠️ Plus `profiles where role = 'client'` : depuis 0053 un livreur, un gérant ou
 * un membre de l'équipe peut commander avec son compte. Est client quiconque a
 * le rôle client, OU a déjà commandé, OU a écrit à l'assistance en tant que
 * client — sans quoi son fil « Client » n'apparaîtrait nulle part.
 */
export async function listerClients(): Promise<Client[]> {
  const { data, error } = await supabase
    .from('clients_plateforme')
    .select('id,full_name,email,phone,created_at')
    .order('created_at', { ascending: false })
  if (error) throw error
  return ((data ?? []) as Array<{
    id: string
    full_name: string | null
    email: string | null
    phone: string | null
    created_at: string
  }>).map((p) => ({
    id: p.id,
    nom: nomLisible(p.full_name, p.email, p.phone, 'Client'),
    email: p.email ?? '',
    telephone: p.phone ?? '',
    creeLe: p.created_at,
  }))
}

// ── Commandes ────────────────────────────────────────────────────────────

const COLONNES_COMMANDE =
  /* ⚠️ `small_order_fee` et `weather_fee` ont été AJOUTÉS ici.
     Ils existent en base, et le moteur comptable les lit pour calculer les
     produits accessoires (compte 707) — mais la requête ne les ramenait pas.
     Ils valaient donc `undefined`, silencieusement traités comme 0 : de vrais
     produits, payés par le client, absents du compte de résultat. */
    'id,ref_code,status,total,delivery_fee,driver_pay,service_fee,small_order_fee,weather_fee,tip,refund_amount,created_at,updated_at,' +
  'accepted_at,preparing_at,ready_at,rider_assigned_at,delivering_at,picked_up_at,delivered_at,cancelled_at,' +
  'delivery_address,delivery_quartier,delivery_commune,delivery_note,delivery_lat,delivery_lng,' +
  'client_id,driver_id,' +
  'merchant:merchants!merchant_id(id,name,lat,lng),' +
  'client:profiles!client_id(full_name,phone),' +
  'driver:profiles!driver_id(full_name),' +
  'order_items(name,quantity,unit_price,total,commission_pct)'

/**
 * Colonnes des migrations 0054 (majoration « Forte demande ») et 0055 (commission
 * ajoutée : les deux prix de chaque ligne), lues seulement si la base les a.
 */
const COLONNES_RECENTES =
  ',tension_fee,tension_driver_pay,modele_commission,' +
  'lignes:order_items(name,quantity,unit_price,total,commission_pct,prix_marchand_unitaire,montant_marchand,montant_commission)'
let baseSans0054 = false

/** « colonne inconnue » sur une colonne de 0054/0055 : la base n'est pas encore à jour, ce n'est pas une panne. */
function colonne0054Absente(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null
  const m = e?.message ?? ''
  return (
    /tension_|modele_commission|prix_marchand|montant_marchand|montant_commission/.test(m) &&
    (e?.code === '42703' || e?.code === 'PGRST204' || /does not exist|could not find/i.test(m))
  )
}

interface LigneCommande {
  id: string
  ref_code: string | null
  status: StatutCommande
  total: number
  delivery_fee: number
  driver_pay: number | null
  tip: number | null
  refund_amount: number | null
  service_fee: number
  small_order_fee: number
  weather_fee: number
  /** 0054 — absentes tant que la migration n'est pas en production. */
  tension_fee?: number | null
  tension_driver_pay?: number | null
  /** 0055 : les lignes avec leurs deux prix (alias de `order_items`, absent sur une base pas à jour). */
  lignes?: Array<
    LigneCommande['order_items'][number] & {
      prix_marchand_unitaire: number | null
      montant_marchand: number | null
      montant_commission: number | null
    }
  >
  created_at: string
  accepted_at: string | null
  preparing_at: string | null
  ready_at: string | null
  rider_assigned_at: string | null
  delivering_at: string | null
  picked_up_at: string | null
  delivered_at: string | null
  cancelled_at: string | null
  delivery_address: string | null
  delivery_quartier: string | null
  delivery_commune: string | null
  delivery_note: string | null
  delivery_lat: number | null
  delivery_lng: number | null
  client_id: string | null
  driver_id: string | null
  merchant: { id: string; name: string; lat: number | null; lng: number | null } | null
  client: { full_name: string | null; phone: string | null } | null
  driver: { full_name: string | null } | null
  order_items: Array<{
    name: string
    quantity: number
    unit_price: number
    total: number | null
    commission_pct: number | null
  }>
}

export async function listerCommandes(): Promise<Commande[]> {
  /* Page par page : au-delà de 1 000 commandes, Supabase tronquait EN SILENCE,
     et la comptabilité — qui se calcule sur cette liste — avec. */
  const lire = (colonnes: string) =>
    toutesLesLignes<unknown>((de, a) =>
      supabase
        .from('orders')
        .select(colonnes)
        .order('created_at', { ascending: false })
        .order('id')
        .range(de, a),
    )
  /* Les colonnes de 0054 sont demandées en plus ; si la base de production ne
     les a pas encore, on relit sans elles (la console peut partir avant la
     migration sans que la liste des commandes — et la compta — tombe). */
  let data: unknown[]
  if (baseSans0054) data = await lire(COLONNES_COMMANDE)
  else {
    try {
      data = await lire(COLONNES_COMMANDE + COLONNES_RECENTES)
    } catch (e) {
      if (!colonne0054Absente(e)) throw e
      baseSans0054 = true
      data = await lire(COLONNES_COMMANDE)
    }
  }

  return ((data as unknown as LigneCommande[] | null) ?? []).map((o) => ({
    id: o.id,
    ref: o.ref_code,
    statut: o.status,
    total: o.total,
    /* Déjà ramenés par la requête, mais jamais exposés : le Live ne pouvait
       calculer ni « livrées aujourd'hui » ni le délai moyen, et la fiche
       commande n'avait aucun suivi pas à pas. */
    accepteeLe: o.accepted_at,
    enPreparationLe: o.preparing_at,
    preteLe: o.ready_at,
    livreurAffecteLe: o.rider_assigned_at,
    retireeLe: o.picked_up_at,
    enLivraisonLe: o.delivering_at,
    livreeLe: o.delivered_at,
    annuleeLe: o.cancelled_at,
    fraisLivraison: o.delivery_fee,
    partLivreur: o.driver_pay,
    fraisService: o.service_fee,
    fraisPetiteCommande: o.small_order_fee,
    fraisMeteo: o.weather_fee,
    fraisTension: o.tension_fee ?? 0,
    partTensionLivreur: o.tension_driver_pay ?? 0,
    pourboire: o.tip ?? 0,
    montantRembourse: o.refund_amount,
    clientId: o.client_id,
    clientNom: o.client?.full_name ?? 'Client',
    clientTelephone: o.client?.phone ?? '',
    clientQuartier: o.delivery_quartier ?? '',
    clientLat: o.delivery_lat,
    clientLng: o.delivery_lng,
    adresseLivraison: o.delivery_address ?? '',
    communeLivraison: o.delivery_commune ?? '',
    noteLivraison: o.delivery_note ?? '',
    marchandId: o.merchant?.id ?? null,
    marchandNom: o.merchant?.name ?? '',
    marchandLat: o.merchant?.lat ?? null,
    marchandLng: o.merchant?.lng ?? null,
    livreurId: o.driver_id,
    livreurNom: o.driver?.full_name ?? null,
    articles: o.lignes ?? o.order_items ?? [],
    creeeLe: o.created_at,
    horodatages: {
      accepted: o.accepted_at,
      preparing: o.preparing_at,
      ready: o.ready_at,
      rider_assigned: o.rider_assigned_at,
      delivering: o.delivering_at,
      picked_up: o.picked_up_at,
      delivered: o.delivered_at,
      cancelled: o.cancelled_at,
    },
  }))
}

/**
 * Affecte un livreur à une commande.
 *
 * ⚠️ Passe par l'edge function : c'est elle qui vérifie que la commande est
 * prête, que le livreur est disponible, et qui pose l'affectation de façon
 * atomique. Un `update` direct depuis le navigateur permettrait d'affecter deux
 * livreurs à la même course.
 */
export async function affecterLivreur(commandeId: string, livreurId: string): Promise<void> {
  if (APERCU) return apercuAffecter(commandeId, livreurId)
  await appelerFonction('set-order-status', {
    order_id: commandeId,
    status: 'rider_assigned',
    driver_id: livreurId,
  })
}

/**
 * Annule une commande AU NOM du dispatch, avec son motif.
 *
 * Tout passe par `set-order-status` : c'est lui qui vérifie que la transition est
 * permise depuis le statut courant, fixe `cancelled_by` et le montant remboursable
 * (le TOTAL, pour une annulation du dispatch), et refuse un motif vide. Les erreurs
 * reviennent déjà traduites en phrases par `appelerFonction`.
 */
export async function annulerCommande(commandeId: string, motif: string): Promise<void> {
  if (APERCU) return apercuAnnuler(commandeId, motif)
  await appelerFonction('set-order-status', {
    order_id: commandeId,
    status: 'cancelled',
    cancel_reason: motif,
  })
}

export async function changerStatutCommande(id: string, statut: StatutCommande): Promise<void> {
  await appelerFonction('set-order-status', { order_id: id, status: statut })
}

/** Les commandes en direct. Le dispatch relit : la charge temps réel n'a pas les jointures. */
export function suivreCommandes(surChangement: () => void): () => void {
  const canal = supabase
    .channel('dispatch-commandes')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, surChangement)
    .subscribe()
  return () => {
    void supabase.removeChannel(canal)
  }
}

export interface PositionLivreur {
  livreurId: string
  lat: number
  lng: number
  releveeLe: string
}

/**
 * Les positions des livreurs, en direct.
 *
 * `driver_locations` est une table en AJOUT SEUL : chaque relevé est une ligne
 * nouvelle et complète. L'événement EST la donnée — inutile de relire.
 */
export function suivrePositions(surPosition: (p: PositionLivreur) => void): () => void {
  const canal = supabase
    .channel('dispatch-positions')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'driver_locations' },
      (charge) => {
        const l = charge.new as { driver_id: string; lat: number; lng: number; recorded_at: string }
        surPosition({ livreurId: l.driver_id, lat: l.lat, lng: l.lng, releveeLe: l.recorded_at })
      },
    )
    .subscribe()
  return () => {
    void supabase.removeChannel(canal)
  }
}

// ── Paiements et remboursements ──────────────────────────────────────────

export async function listerPaiements(): Promise<Paiement[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*, order:orders!order_id(ref_code), client:profiles!client_id(full_name)')
    .order('created_at', { ascending: false })
  if (error) throw error
  type Ligne = {
    id: string
    provider_ref: string | null
    order_id: string | null
    kind?: string | null
    provider: string
    amount: number
    status: string
    created_at: string
    order: { ref_code: string | null } | null
    client: { full_name: string | null } | null
  }
  return ((data as unknown as Ligne[] | null) ?? []).map((p) => ({
    id: p.id,
    ref: p.provider_ref ?? p.id,
    /* Paiement d'abonnement Premium (0062) : pas de commande. */
    refCommande: p.kind === 'premium' ? 'Abonnement Premium' : p.order?.ref_code ?? '',
    commandeId: p.order_id,
    clientNom: p.client?.full_name ?? 'Client',
    fournisseur: p.provider === 'orange_money' ? 'om' : 'wave',
    montant: p.amount,
    statut: p.status,
    creeLe: p.created_at,
  }))
}

export async function listerRemboursements(): Promise<Remboursement[]> {
  const { data, error } = await supabase
    .from('orders')
    .select(
      'id,ref_code,total,refund_amount,cancelled_by,cancelled_at,cancel_reason, ' +
        'client:profiles!client_id(full_name), payments(id,status,provider,amount)',
    )
    .not('refund_amount', 'is', null)
    .gt('refund_amount', 0)
    .eq('status', 'cancelled')
    .order('cancelled_at', { ascending: false })
  if (error) throw error

  type Ligne = {
    id: string
    ref_code: string | null
    total: number
    refund_amount: number
    cancelled_by: string | null
    cancelled_at: string | null
    cancel_reason: string | null
    client: { full_name: string | null } | null
    payments: Array<{ id: string; status: string; provider: string; amount: number }>
  }

  return ((data as unknown as Ligne[] | null) ?? []).map((o) => {
    const paiements = o.payments ?? []
    // On cherche d'abord un paiement RÉUSSI (donc remboursable), puis un déjà
    // remboursé (pour afficher « fait »). L'ordre compte : l'inverse
    // masquerait un remboursement encore dû derrière un ancien déjà réglé.
    const p = paiements.find((x) => x.status === 'succeeded') ??
      paiements.find((x) => x.status === 'refunded') ?? null
    return {
      commandeId: o.id,
      ref: o.ref_code,
      client: o.client?.full_name ?? 'Client',
      total: o.total,
      montant: o.refund_amount,
      annulePar: o.cancelled_by,
      motif: o.cancel_reason,
      annuleLe: o.cancelled_at,
      paiementId: p?.id ?? null,
      fournisseur: p ? (p.provider === 'orange_money' ? 'Orange Money' : 'Wave') : '—',
      fait: p?.status === 'refunded',
      payable: p?.status === 'succeeded',
    }
  })
}

/**
 * Marque un remboursement comme EXÉCUTÉ, après virement manuel chez le PSP.
 *
 * Le `eq('status','succeeded')` n'est pas décoratif : il empêche de marquer
 * deux fois le même remboursement si l'opérateur clique en double.
 */
export async function marquerRembourse(paiementId: string): Promise<void> {
  const { error } = await supabase
    .from('payments')
    .update({ status: 'refunded' })
    .eq('id', paiementId)
    .eq('status', 'succeeded')
  if (error) throw error
}

// ── Paie ─────────────────────────────────────────────────────────────────

export async function listerPaieLivreurs(): Promise<{
  aPayer: PaieLivreur[]
  historique: PeriodePayee[]
}> {
  if (APERCU) return { aPayer: [], historique: [] }
  const { data, error } = await supabase
    .from('driver_payouts')
    .select('*, driver:drivers!driver_id(profile:profiles!id(full_name))')
  if (error) throw error

  type Ligne = {
    driver_id: string
    delivery_count: number
    base_earnings: number
    weather_bonus: number
    peak_bonus: number
    volume_bonus: number
    loyalty_bonus: number
    /** 0060 — absentes (undefined) tant que la migration n'est pas en base. */
    peak_deliveries?: number | null
    volume_days?: number | null
    loyalty_months?: number | null
    tips: number
    total: number
    period_start: string
    period_end: string
    status: string
    driver: { profile: { full_name: string | null } | null } | null
  }

  const lignes = (data as unknown as Ligne[] | null) ?? []

  const aPayer: PaieLivreur[] = lignes
    .filter((p) => p.status !== 'paid')
    .map((p) => ({
      livreurId: p.driver_id,
      livreurNom: p.driver?.profile?.full_name ?? 'Livreur',
      courses: p.delivery_count,
      base: p.base_earnings,
      bonusMeteo: p.weather_bonus,
      bonusPointe: p.peak_bonus,
      bonusVolume: p.volume_bonus,
      bonusFidelite: p.loyalty_bonus,
      coursesPointe: p.peak_deliveries ?? 0,
      joursVolume: p.volume_days ?? 0,
      moisFidelite: p.loyalty_months ?? 0,
      pourboires: p.tips,
      total: p.total,
      debut: p.period_start,
      fin: p.period_end,
      statut: 'en attente',
    }))

  const parPeriode: Record<string, PeriodePayee> = {}
  for (const p of lignes.filter((x) => x.status === 'paid')) {
    const cle = `${p.period_start} — ${p.period_end}`
    const e = (parPeriode[cle] ??= {
      periode: cle,
      livreurs: 0,
      total: 0,
      bonusPointe: 0,
      bonusVolume: 0,
      bonusFidelite: 0,
    })
    e.livreurs++
    e.total += p.total
    e.bonusPointe += p.peak_bonus ?? 0
    e.bonusVolume += p.volume_bonus ?? 0
    e.bonusFidelite += p.loyalty_bonus ?? 0
  }

  return { aPayer, historique: Object.values(parPeriode) }
}

/**
 * Recalcule les lots de paie sur une période.
 *
 * ⚠️ Le calcul est fait par le SERVEUR (RPC). Le rejouer en TypeScript
 * donnerait une seconde vérité sur ce qu'on doit à des gens — c'est exactement
 * le genre de divergence qui se découvre le jour de la paie.
 */
export async function calculerPaieLivreurs(debut: string, fin: string): Promise<void> {
  const { error } = await supabase.rpc('compute_driver_payouts', { p_start: debut, p_end: fin })
  if (error) throw error
}

// ── Messagerie ───────────────────────────────────────────────────────────

/**
 * Les fils d'ASSISTANCE, regroupés par (personne, casquette) — clé `cleFil`.
 *
 * Depuis la migration 0053, chaque message dit au nom de quel rôle on écrit :
 * une personne à la fois livreur et cliente a deux fils, et le classement ne
 * dépend plus d'une liste de membres du staff (voir `regrouperFils`). Les
 * échanges client ↔ livreur s'affichent à part (`listerDiscussionsClientLivreur`).
 */
export async function listerFils(): Promise<FilsParPair> {
  const { data, error } = await supabase
    .from('chat_messages')
    .select(COLONNES_CHAT)
    .order('created_at')
  if (error) throw error
  return regrouperFils((data ?? []) as unknown as LigneChat[])
}

const PAR_LOT = 50

/**
 * Échanges CLIENT ↔ LIVREUR, regroupés par commande — LECTURE SEULE.
 *
 * Le dispatch les lit pour arbitrer un litige (policies staff de lecture,
 * migration 0040). Rien ici n'écrit : aucune fonction d'envoi n'existe pour ces
 * fils, et l'écran n'en propose pas.
 *
 * `idsCommandes` restreint la lecture à certaines commandes (la fiche d'une
 * commande ne veut que la sienne). Sans lui, on lit les 1 000 derniers messages
 * liés à une commande : l'historique complet grossirait sans fin, et le
 * dispatch n'arbitre que des litiges récents.
 */
export async function listerDiscussionsClientLivreur(
  idsCommandes?: readonly string[],
): Promise<DiscussionCommande[]> {
  let requete = supabase
    .from('chat_messages')
    .select(COLONNES_CHAT)
    .not('order_id', 'is', null)
    .not('recipient_id', 'is', null)
    .not('casquette_destinataire', 'is', null)
  if (idsCommandes) requete = requete.in('order_id', [...idsCommandes])
  const { data, error } = await requete.order('created_at', { ascending: false }).limit(1000)
  if (error) throw error

  const echanges = ((data ?? []) as unknown as LigneChat[]).filter((m) => estEchangeClientLivreur(m))
  const ids = [...new Set(echanges.map((m) => m.order_id as string))]
  if (ids.length === 0) return []

  // Par lots : `in.(…)` met chaque identifiant dans l'URL, et quelques centaines
  // de UUID la feraient dépasser la limite du serveur.
  const lots: string[][] = []
  for (let i = 0; i < ids.length; i += PAR_LOT) lots.push(ids.slice(i, i + PAR_LOT))
  const reponses = await Promise.all(
    lots.map((lot) =>
      supabase
        .from('orders')
        .select(
          'id,ref_code,client_id,driver_id,client:profiles!client_id(full_name),driver:profiles!driver_id(full_name)',
        )
        .in('id', lot),
    ),
  )
  const commandes = new Map<string, InfosCommande>()
  for (const r of reponses) {
    if (r.error) throw r.error
    for (const o of (r.data ?? []) as unknown as Array<{
      id: string
      ref_code: string | null
      client_id: string | null
      driver_id: string | null
      client: { full_name: string | null } | null
      driver: { full_name: string | null } | null
    }>) {
      commandes.set(o.id, {
        id: o.id,
        ref: o.ref_code,
        clientId: o.client_id,
        livreurId: o.driver_id,
        clientNom: o.client?.full_name ?? 'Client',
        livreurNom: o.driver?.full_name ?? 'Livreur',
      })
    }
  }
  return regrouperDiscussions(echanges, commandes)
}

/** Nombre de messages client ↔ livreur d'UNE commande (0 = aucun fil à montrer). */
export async function nombreMessagesClientLivreur(orderId: string): Promise<number> {
  const [fil] = await listerDiscussionsClientLivreur([orderId])
  return fil?.messages.length ?? 0
}

/**
 * Répond dans UN fil d'assistance : à cette personne, DANS L'APP de cette
 * casquette (`casquette_destinataire`). Sans elle, la réponse à un livreur qui
 * a écrit depuis l'app client partirait dans son app Coursier.
 */
export async function envoyerMessage(cle: string, texte: string): Promise<void> {
  const fil = lireCleFil(cle)
  if (!fil) throw new Error('Conversation introuvable — rechargez la page.')
  const { data: auth } = await supabase.auth.getUser()
  const moi = auth.user?.id
  if (!moi) throw new Error('Session expirée — reconnectez-vous.')
  const { error } = await supabase.from('chat_messages').insert({
    sender_id: moi,
    recipient_id: fil.personne,
    casquette_destinataire: fil.casquette,
    body: texte,
  })
  if (error) {
    // Refus du déclencheur `chat_casquettes` (0053) : la personne n'a plus ce rôle (fiche livreur retirée,
    // équipier sorti de l'équipe…). On le dit plutôt que d'afficher le code brut.
    if (/casquette_destinataire_invalide/.test(error.message)) {
      throw new Error("Cette personne n'a plus ce rôle : écrivez-lui depuis une autre catégorie (Client, Livreur ou Marchand).")
    }
    throw error
  }
}

export async function marquerFilLu(cle: string): Promise<void> {
  const fil = lireCleFil(cle)
  if (!fil) return
  /* ⚠️ On ne marque « lus » que les messages ADRESSÉS au dispatch
     (`casquette_destinataire` nulle) PAR CETTE PERSONNE SOUS CETTE CASQUETTE.
     Ni ses messages à son livreur ou à son client (fil de commande : le
     destinataire perdrait son indicateur de non-lu sans avoir ouvert), ni ceux
     de son autre casquette (un fil « Client » ouvert ne vaut pas lecture du fil
     « Livreur »). */
  await supabase
    .from('chat_messages')
    .update({ read_at: new Date().toISOString() })
    .eq('sender_id', fil.personne)
    .eq('casquette_expediteur', fil.casquette)
    .is('casquette_destinataire', null)
    .is('read_at', null)
}

export function suivreMessages(surChangement: () => void): () => void {
  const canal = supabase
    .channel('dispatch-chat')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, surChangement)
    .subscribe()
  return () => {
    void supabase.removeChannel(canal)
  }
}

// ── Configuration ────────────────────────────────────────────────────────

export async function chargerConfiguration(): Promise<Configuration> {
  if (APERCU) return structuredClone(apercuConfig())
  const [cfg, paliers, meteo] = await Promise.all([
    supabase.from('platform_config').select('*').eq('id', 1).single(),
    supabase.from('delivery_pricing').select('*').order('radius_min_km'),
    supabase.from('weather_bonuses').select('*'),
  ])
  if (cfg.error) throw cfg.error

  const c = cfg.data as unknown as {
    service_fee: number
    small_order_threshold: number
    small_order_fee: number
    free_delivery_threshold: number
    peak_lunch_start: string
    peak_lunch_end: string
    peak_dinner_start: string
    peak_dinner_end: string
    peak_bonus: number
    volume_threshold: number
    volume_bonus: number
    loyalty_threshold: number
    loyalty_bonus: number
    premium_monthly_price: number
    payout_day_1: number
    payout_day_2: number
    auto_rating: number | string | null
    default_commission_pct: number | null
    vat_enabled: boolean | null
    vat_rate: number | null
    /* Migration 0043. `select('*')` ne lève RIEN si ces colonnes manquent : elles
       sont simplement absentes de la ligne (undefined) — c'est ce qui sert à
       détecter une base pas encore mise à jour. */
    meteo_actuelle?: string | null
    frais_traversee?: number | null
    bonus_traversee?: number | null
    /* Migration 0044 — absentes tant qu'elle n'est pas poussée. */
    frais_mm_encaissement_wave?: number | string | null
    frais_mm_encaissement_om?: number | string | null
    frais_mm_decaissement_wave?: number | string | null
    frais_mm_decaissement_om?: number | string | null
    /* Migration 0054 — absentes tant qu'elle n'est pas poussée. */
    service_fee_haut?: number | null
    premium_reduction_pct?: number | string | null
    commande_minimum?: number | null
    livraison_min_client?: number | null
    livraison_min_premium?: number | null
    livraison_min_livreur?: number | null
    tension_active?: boolean | null
    tension_rayon_km?: number | string | null
    tension_seuil?: number | string | null
    tension_pente_pct?: number | string | null
    tension_plafond_pct?: number | string | null
    tension_part_livreur_pct?: number | string | null
  }

  return {
    fraisService: c.service_fee,
    seuilPetiteCommande: c.small_order_threshold,
    fraisPetiteCommande: c.small_order_fee,
    seuilLivraisonOfferte: c.free_delivery_threshold,
    pointeMidi: `${hhmm(c.peak_lunch_start)}-${hhmm(c.peak_lunch_end)}`,
    pointeSoir: `${hhmm(c.peak_dinner_start)}-${hhmm(c.peak_dinner_end)}`,
    bonusPointe: c.peak_bonus,
    seuilVolume: c.volume_threshold,
    bonusVolume: c.volume_bonus,
    seuilFidelite: c.loyalty_threshold,
    bonusFidelite: c.loyalty_bonus,
    premiumMensuel: c.premium_monthly_price,
    jourPaie1: c.payout_day_1,
    jourPaie2: c.payout_day_2,
    noteAuto: noteAutoDepuisBase(c.auto_rating),
    commissionParDefaut: Math.round((c.default_commission_pct ?? 0) * 100),
    tvaActive: Boolean(c.vat_enabled),
    /* Un taux peut porter une décimale (5,5 %) : arrondir à l'entier comme la
       commission écraserait un taux réduit. */
    tvaTaux: Number((Number(c.vat_rate ?? 0) * 100).toFixed(2)),
    fraisMM: {
      encaissementWave: enPourcent(c.frais_mm_encaissement_wave),
      encaissementOm: enPourcent(c.frais_mm_encaissement_om),
      decaissementWave: enPourcent(c.frais_mm_decaissement_wave),
      decaissementOm: enPourcent(c.frais_mm_decaissement_om),
    },
    /* `numeric(4,1)` revient en nombre OU en texte selon le pilote : on force. */
    paliersLivraison: ((paliers.data ?? []) as Array<{
      id: string | number
      radius_min_km: number | string
      radius_max_km: number | string
      client_fee: number
      driver_pay: number
      premium_fee: number | null
    }>).map((r) => ({
      id: String(r.id),
      libelle: `${Number(r.radius_min_km)} — ${Number(r.radius_max_km)} km`,
      minKm: Number(r.radius_min_km),
      maxKm: Number(r.radius_max_km),
      fraisClient: r.client_fee,
      partLivreur: r.driver_pay,
      fraisPremium: r.premium_fee ?? 0,
    })),
    bonusMeteo: ((meteo.data ?? []) as Array<{
      id: string | number
      condition: string
      driver_bonus: number
      client_surcharge: number
      premium_exempt: boolean | null
    }>).map((w) => ({
      id: String(w.id),
      condition: w.condition,
      bonusLivreur: w.driver_bonus,
      supplementClient: w.client_surcharge,
      premiumExempt: w.premium_exempt !== false,
    })),
    meteoActuelle: estConditionMeteo(c.meteo_actuelle) ? c.meteo_actuelle : null,
    minimaLivraison:
      typeof c.livraison_min_client === 'number'
        ? {
            client: c.livraison_min_client,
            premium: Number(c.livraison_min_premium ?? 0),
            driver: Number(c.livraison_min_livreur ?? 0),
          }
        : null,
    fraisServiceHaut: typeof c.service_fee_haut === 'number' ? c.service_fee_haut : null,
    reductionPremium: c.premium_reduction_pct == null ? null : Number(c.premium_reduction_pct),
    commandeMinimum: typeof c.commande_minimum === 'number' ? c.commande_minimum : null,
    tension:
      typeof c.tension_active === 'boolean'
        ? {
            active: c.tension_active,
            rayonKm: Number(c.tension_rayon_km),
            seuil: Number(c.tension_seuil),
            pentePct: Number(c.tension_pente_pct),
            plafondPct: Number(c.tension_plafond_pct),
            partLivreurPct: Number(c.tension_part_livreur_pct),
          }
        : null,
    fraisTraversee: typeof c.frais_traversee === 'number' ? c.frais_traversee : null,
    bonusTraversee: typeof c.bonus_traversee === 'number' ? c.bonus_traversee : null,
  }
}

// ── Équipe ───────────────────────────────────────────────────────────────

export async function listerStaff(): Promise<MembreStaff[]> {
  const { data, error } = await supabase
    .from('staff_members')
    // `staff_members` a DEUX clés étrangères vers `profiles` (profile_id et invited_by) : sans indice,
    // PostgREST répond 300 PGRST201 (embed ambigu) et l'écran Équipe reste vide. On nomme la bonne.
    .select('id,profile_id,role,status,created_at,title,permissions, profiles!staff_members_profile_id_fkey!inner(full_name,email,phone)')
    .order('created_at')
  if (error) throw error
  type Ligne = {
    id: string
    profile_id: string
    role: string
    status: string
    created_at: string
    title: string | null
    permissions: Record<string, boolean> | null
    profiles: { full_name: string | null; email: string | null; phone: string | null } | null
  }
  return ((data as unknown as Ligne[] | null) ?? []).map((s) => ({
    id: s.id,
    profileId: s.profile_id,
    nom: s.profiles?.full_name ?? '—',
    email: s.profiles?.email ?? '',
    telephone: s.profiles?.phone ?? '',
    role: s.role,
    statut: s.status,
    creeLe: s.created_at,
    intitule: s.title ?? '',
    permissions: s.permissions ?? {},
  }))
}

/** Le rôle du staff connecté — il décide des onglets visibles. */
export async function monRoleStaff(): Promise<string | null> {
  const { data: auth } = await supabase.auth.getUser()
  const moi = auth.user?.id
  if (!moi) return null
  const { data } = await supabase
    .from('staff_members')
    .select('role')
    .eq('profile_id', moi)
    .eq('status', 'active')
    .maybeSingle<{ role: string }>()
  return data?.role ?? null
}

// ── Droits d'équipe ──────────────────────────────────────────────────────

/**
 * Les dix droits de la console, dans l'ordre de l'original.
 *
 * ⚠️ Ce ne sont pas des étiquettes : `staff_role_permissions` porte les mêmes
 * clés et la fonction SQL `has_perm()` s'en sert derrière la RLS. Renommer une
 * clé ici sans migrer la table ouvre un écran que le serveur refusera de
 * remplir — l'utilisateur verrait un tableau vide sans explication.
 */
export const DROITS = [
  { cle: 'voir_live', libelle: 'Voir le Live' },
  { cle: 'gerer_courses', libelle: 'Gérer les courses' },
  { cle: 'gerer_livreurs', libelle: 'Gérer les livreurs' },
  { cle: 'gerer_marchands', libelle: 'Gérer les marchands' },
  { cle: 'voir_paiements', libelle: 'Voir les paiements' },
  { cle: 'gerer_paie', libelle: 'Gérer la paie' },
  { cle: 'voir_compta', libelle: 'Voir la compta' },
  { cle: 'exporter_compta', libelle: 'Exporter la compta' },
  { cle: 'gerer_config', libelle: 'Modifier les tarifs' },
  { cle: 'gerer_equipe', libelle: "Gérer l'équipe" },
] as const

export type CleDroit = (typeof DROITS)[number]['cle']
export type Droits = Partial<Record<CleDroit, boolean>>
export type DroitsParRole = Record<string, CleDroit[]>

export const LIBELLE_ROLE: Record<string, string> = {
  admin: 'Admin',
  dispatcher: 'Dispatch',
  comptable: 'Comptable',
  support: 'Support',
}

/** Droits accordés par rôle — même table que `has_perm()` derrière la RLS. */
export async function listerDroitsParRole(): Promise<DroitsParRole> {
  const { data, error } = await supabase.from('staff_role_permissions').select('role,perm')
  if (error) throw error
  const sortie: DroitsParRole = { admin: [], dispatcher: [], comptable: [], support: [] }
  for (const r of (data ?? []) as Array<{ role: string; perm: CleDroit }>) {
    ;(sortie[r.role] ??= []).push(r.perm)
  }
  return sortie
}

/**
 * Droits RÉELS d'un membre : ceux de son rôle, puis les écarts individuels
 * par-dessus. Un écart peut RETIRER un droit du rôle, pas seulement en ajouter
 * — d'où la fusion dans cet ordre et non une simple union.
 */
export function droitsEffectifs(
  /* `role` peut être NUL : un compte hors équipe, ou suspendu. Il n'a alors
     aucun droit de rôle — mais un écart individuel resterait applicable. */
  membre: { role: string | null; permissions?: Droits | null },
  parRole: DroitsParRole,
): Droits {
  const base: Droits = {}
  for (const k of (membre.role ? parRole[membre.role] : null) ?? []) base[k] = true
  return { ...base, ...(membre.permissions ?? {}) }
}

export interface MonStaff {
  profileId: string | null
  role: string | null
  permissions: Droits
}

/** Ma propre fiche d'équipe : elle décide de ce que la console affiche. */
export async function monStaff(): Promise<MonStaff> {
  const { data: auth } = await supabase.auth.getUser()
  const moi = auth.user?.id ?? null
  if (!moi) return { profileId: null, role: null, permissions: {} }
  const { data, error } = await supabase
    .from('staff_members')
    .select('id,role,permissions,status')
    .eq('profile_id', moi)
    .maybeSingle<{ role: string; permissions: Droits | null; status: string }>()
  if (error) throw error
  /* Un membre suspendu n'a aucun droit. La RLS ferme déjà la porte côté
     serveur ; ici on évite surtout de lui ouvrir des écrans qui ne
     renverraient que des tableaux vides, sans lui dire pourquoi. */
  if (!data || data.status !== 'active') return { profileId: moi, role: null, permissions: {} }
  return { profileId: moi, role: data.role, permissions: data.permissions ?? {} }
}

/**
 * La personne connectée appartient-elle au personnel (admin / dispatcher) ?
 *
 * Même règle que `is_staff()` côté base — c'est elle qui ouvre les policies RLS
 * du dispatch. `null` = lecture impossible (on ne sait pas : on n'enferme pas
 * dehors le dispatch de garde sur un incident de lecture, la RLS tient).
 * Sert à refuser un compte qui n'a PAS accès à cette console, qu'il soit venu
 * par mot de passe ou par « Continuer avec Google » (qui ne donne aucun rôle).
 */
export async function estDuPersonnel(): Promise<boolean | null> {
  const { data: auth } = await supabase.auth.getUser()
  const moi = auth.user?.id ?? null
  if (!moi) return false
  const { data, error } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', moi)
    .maybeSingle<{ role: string | null }>()
  if (error) return null
  return data?.role === 'admin' || data?.role === 'dispatcher'
}

export async function inviterMembre(m: {
  nom: string
  contact: ContactValide
  role: string
  intitule: string | null
  permissions: Droits
}): Promise<ReponseCreation | null> {
  return appelerFonction('invite-member', corpsInvitationMembre(m))
}

export async function modifierMembre(
  id: string,
  patch: { role?: string; title?: string; permissions?: Droits; status?: string },
): Promise<void> {
  await appelerFonction('set-member-role', { membership_id: id, ...patch })
}

// ── Création de comptes ──────────────────────────────────────────────────

/**
 * Réponse des edge functions de création de compte. `temp_password` est `null` quand le compte existait déjà ET quand
 * la personne est identifiée par son seul numéro WhatsApp (`connexion: 'whatsapp'`, pas de mot de passe).
 */
export interface Identifiants extends ReponseCreation {
  email?: string | null
  temp_password: string | null
  compte_existant?: boolean
}

/**
 * ⚠️ Le téléphone d'un CONTACT (fiche établissement) est NORMALISÉ avant l'envoi : on retire tout ce qui n'est ni
 * chiffre ni « + », puis on préfixe si le « + » manque. Sans ça, « 75 08 36 64 » et « +22375083664 » créent deux
 * contacts pour la même personne. (Le numéro WhatsApp d'un COMPTE, lui, passe par `validerContact` : E.164 strict.)
 */
const telephoneNormalise = (t: string) => t.replace(/[^\d+]/g, '').replace(/^([^+])/, '+$1')

export async function creerCompteLivreur(r: {
  nom: string
  contact: ContactValide
  typeVehicule: string
  plaque: string
  modele: string
  fournisseurPaiement: string
  numeroPaiement: string
}): Promise<Identifiants> {
  return appelerFonction('create-driver-account', corpsCreationLivreur(r))
}

/**
 * ⚠️ DEUX noms de colonnes que j'avais inventés, et qui auraient fait échouer
 * toute création de marchand :
 *   • le type d'établissement est `kind`, pas `type` — la LECTURE le mappait
 *     déjà correctement, seule l'écriture avait été écrite de mémoire ;
 *   • les notes vont dans `description` ; il n'existe pas de colonne `notes`.
 */
export async function ajouterMarchand(r: {
  nom: string
  type: string
  quartier: string
  telephone: string
  notes: string
}): Promise<{ id: string; name: string }> {
  const { data, error } = await supabase
    .from('merchants')
    .insert({
      name: r.nom,
      kind: kindDepuisSaisie(r.type),
      quartier: r.quartier,
      phone: telephoneNormalise(r.telephone),
      description: r.notes,
    })
    .select('id,name')
    .single<{ id: string; name: string }>()
  if (error) throw error
  return data
}

export async function creerCompteMarchand(m: {
  marchandId: string
  contact: ContactValide
  nomGerant: string
}): Promise<Identifiants> {
  return appelerFonction('create-merchant-account', corpsCreationMarchand(m))
}

/**
 * Équipes des marchands — nécessaire pour que le dispatch puisse répondre à un
 * MANAGER, et pas seulement au gérant. Sans cette liste, un message envoyé par
 * un manager n'a aucun interlocuteur joignable en retour.
 */
export interface EquipierMarchand {
  marchandId: string
  profileId: string
  role: string
  nom: string
}

export async function listerEquipesMarchands(): Promise<EquipierMarchand[]> {
  const { data, error } = await supabase
    .from('merchant_staff')
    .select('merchant_id,profile_id,role,status, profiles!inner(full_name)')
    .eq('status', 'active')
  if (error) throw error
  type Ligne = {
    merchant_id: string
    profile_id: string
    role: string
    profiles: { full_name: string | null } | null
  }
  return ((data as unknown as Ligne[] | null) ?? []).map((s) => ({
    marchandId: s.merchant_id,
    profileId: s.profile_id,
    role: s.role,
    nom: s.profiles?.full_name ?? '',
  }))
}

/**
 * Suspension d'un établissement — décidée par SwiftEats, et que le marchand ne
 * peut PAS lever lui-même.
 *
 * ⚠️ Ce n'est pas « fermer » : la fermeture ordinaire suit les horaires que le
 * marchand a réglés et sa pause éventuelle. Ici on pose un blocage MOTIVÉ, et
 * le motif part tel quel dans son application — il doit donc être lisible par
 * lui, pas une note interne.
 */
export async function definirBlocageMarchand(id: string, motif: string | null): Promise<void> {
  const { data: auth } = await supabase.auth.getUser()
  const patch = motif
    ? { blocked_reason: motif, blocked_at: new Date().toISOString(), blocked_by: auth.user?.id ?? null }
    : { blocked_reason: null, blocked_at: null, blocked_by: null }
  const { error } = await supabase.from('merchants').update(patch).eq('id', id)
  if (error) throw error
}

/**
 * ⚠️ Cette fonction n'EXÉCUTE aucun virement — il n'existe pas d'API
 * Wave ou Orange branchée. Elle enregistre que le virement a été fait à la
 * main. Marquer sans avoir viré laisse quelqu'un sans son argent, et la trace
 * comptable affirme le contraire. (Les marchands : écran Reversements, `api/reversements.ts`.)
 */
export async function marquerLivreursPayes(): Promise<void> {
  const { error } = await supabase
    .from('driver_payouts')
    .update({ status: 'paid', paid_at: new Date().toISOString() })
    .eq('status', 'pending')
  if (error) throw error
}

// ── Configuration : écriture ─────────────────────────────────────────────

/** Réglages stockés tels quels, en nombre. */
const REGLAGES_SIMPLES = new Set([
  'service_fee', 'small_order_threshold', 'small_order_fee', 'free_delivery_threshold',
  'peak_bonus', 'volume_threshold', 'volume_bonus', 'loyalty_threshold', 'loyalty_bonus',
  'premium_monthly_price',
  /* Migration 0043 : suppléments « traversée du Niger ». */
  'frais_traversee', 'bonus_traversee',
  /* Migration 0055 : sous-total client minimum pour commander. */
  'commande_minimum',
])

/** Aucun réglage « simple » n'a de décimale : `auto_rating` (4,5) a sa propre branche. */
const REGLAGES_DECIMAUX = new Set<string>()

/**
 * Majoration de tension (0054) : nombres à décimales, avec leurs bornes (les
 * mêmes que les contraintes de la base, dites en une phrase avant l'envoi).
 */
const REGLAGES_TENSION: Record<string, { min: number; max: number; minExclu?: boolean; libelle: string }> = {
  tension_rayon_km: { min: 0, max: 50, minExclu: true, libelle: 'Rayon attendu entre 0 et 50 km.' },
  tension_seuil: { min: 0, max: 999, libelle: 'Seuil attendu : un ratio positif (1 = autant de commandes que de livreurs).' },
  tension_pente_pct: { min: 0, max: 200, libelle: 'Pente attendue entre 0 et 200 % par point de ratio.' },
  tension_plafond_pct: { min: 0, max: 100, libelle: 'Plafond attendu entre 0 et 100 %.' },
  tension_part_livreur_pct: { min: 0, max: 100, libelle: 'Part livreur attendue entre 0 et 100 %.' },
  /* 0055 : la livraison Premium = prix client × (1 − réduction). */
  premium_reduction_pct: { min: 0, max: 100, libelle: 'Réduction Premium attendue entre 0 et 100 %.' },
}

/** Saisis comme « 12:00-14:00 », stockés en deux colonnes. */
const REGLAGES_PLAGES: Record<string, [string, string]> = {
  peak_lunch: ['peak_lunch_start', 'peak_lunch_end'],
  peak_dinner: ['peak_dinner_start', 'peak_dinner_end'],
}

const COLONNES_FRAIS_MM = new Set([
  'frais_mm_encaissement_wave',
  'frais_mm_encaissement_om',
  'frais_mm_decaissement_wave',
  'frais_mm_decaissement_om',
])

/** Fraction en base → pourcentage à l'écran ; garde `null` et `undefined`. */
function enPourcent(v: number | string | null | undefined): number | null | undefined {
  if (v === undefined) return undefined
  if (v === null || v === '') return null
  return Number((Number(v) * 100).toFixed(2))
}

/**
 * Enregistre UN réglage de plateforme.
 *
 * ⚠️ Ces valeurs fixent nos barèmes : ce que touchent le client, le livreur et
 * le marchand. Le contrôle de saisie n'est donc pas cosmétique — sans lui on
 * peut ranger du texte dans « Frais de service », et chaque commande suivante
 * facture n'importe quoi.
 *
 * L'écran n'affiche la nouvelle valeur qu'APRÈS confirmation du serveur : une
 * version antérieure écrivait localement d'abord et laissait l'erreur passer
 * en silence, montrant un barème que la base n'avait pas accepté.
 */
export async function enregistrerReglage(cle: string, valeur: string): Promise<void> {
  if (APERCU) return apercuAppliquer({ [cle]: valeur })
  const col = cle === 'premium_monthly' ? 'premium_monthly_price' : cle
  // `boolean` depuis l'assujettissement TVA : la colonne est un booléen
  // Postgres, et PostgREST refuse 1/0 à sa place.
  let patch: Record<string, string | number | boolean | null>

  if (col === 'auto_rating') {
    /* Une note de 1 à 5 à une décimale — la base refuse le reste (check 0027) ;
       on le dit avant, en une phrase, plutôt que de renvoyer l'erreur Postgres. */
    patch = { auto_rating: validerNoteAuto(valeur) }
  } else if (REGLAGES_SIMPLES.has(col)) {
    const n = Number(String(valeur).replace(/\s/g, '').replace(',', '.'))
    if (!Number.isFinite(n) || n < 0) throw new Error('Valeur numérique positive attendue.')
    patch = { [col]: REGLAGES_DECIMAUX.has(col) ? n : Math.round(n) }
  } else if (REGLAGES_TENSION[col]) {
    const b = REGLAGES_TENSION[col]
    const n = Number(String(valeur).replace(/\s/g, '').replace(',', '.'))
    if (!Number.isFinite(n) || n > b.max || (b.minExclu ? n <= b.min : n < b.min)) throw new Error(b.libelle)
    patch = { [col]: Math.round(n * 100) / 100 }
  } else if (col === 'tension_active') {
    if (valeur !== 'oui' && valeur !== 'non') throw new Error('Valeur attendue : oui ou non.')
    patch = { tension_active: valeur === 'oui' }
  } else if (col === 'vat_enabled') {
    /* Un booléen saisi par liste déroulante : on n'accepte que les deux
       valeurs attendues. Interpréter une chaîne inconnue comme « faux »
       désassujettirait l'entreprise sur une faute de frappe. */
    if (valeur !== 'oui' && valeur !== 'non') throw new Error('Valeur attendue : oui ou non.')
    patch = { vat_enabled: valeur === 'oui' }
  } else if (COLONNES_FRAIS_MM.has(col)) {
    /* Frais d'opérateur : POURCENTAGE à l'écran, FRACTION en base. Vide =
       « pas encore connu » (NULL), que la comptabilité signale au lieu de
       compter zéro franc de frais. Plafond à 10 % : au-delà, c'est presque
       sûrement un taux saisi en fraction (0,01) pris pour un pourcentage. */
    const brut = String(valeur).trim()
    if (brut === '') patch = { [col]: null }
    else {
      const pct = Number(brut.replace(',', '.'))
      if (!Number.isFinite(pct) || pct < 0 || pct > 10) throw new Error('Taux attendu entre 0 et 10 %, ou vide si inconnu.')
      patch = { [col]: Number((pct / 100).toFixed(4)) }
    }
  } else if (col === 'vat_rate') {
    /* Saisi en POURCENTAGE, stocké en FRACTION — même piège que la commission :
       écrire 18 au lieu de 0,18 multiplierait la taxe par cent. Le plafond à
       30 % laisse la place à une hausse ou à un taux d'un autre pays de
       l'UEMOA, sans laisser passer un 18 mal placé. */
    const pct = Number(String(valeur).replace(',', '.'))
    if (!Number.isFinite(pct) || pct < 0 || pct > 30) throw new Error('Taux attendu entre 0 et 30 %.')
    patch = { vat_rate: pct / 100 }
  } else if (col === 'default_commission_pct') {
    /* Saisi en POURCENTAGE, stocké en FRACTION : la base porte 0.15, pas 15.
       Écrire 15 ferait une commission de 1500 %. */
    const pct = Number(valeur)
    if (!Number.isFinite(pct) || pct < 0 || pct > 50) throw new Error('Taux attendu entre 0 et 50 %.')
    patch = { default_commission_pct: pct / 100 }
  } else if (REGLAGES_PLAGES[col]) {
    const [a, b] = String(valeur).split('-').map((s) => s.trim())
    if (!/^\d{2}:\d{2}$/.test(a ?? '') || !/^\d{2}:\d{2}$/.test(b ?? ''))
      throw new Error('Format attendu : 12:00-14:00')
    const [cA, cB] = REGLAGES_PLAGES[col]
    patch = { [cA]: a!, [cB]: b! }
  } else if (col === 'payout_day_1' || col === 'payout_day_2') {
    const d = parseInt(valeur, 10)
    if (!(d >= 1 && d <= 31)) throw new Error('Jour du mois attendu entre 1 et 31.')
    patch = { [col]: d }
  } else {
    throw new Error(`Réglage inconnu : ${cle}`)
  }

  const { error } = await supabase.from('platform_config').update(patch).eq('id', 1)
  if (error) throw erreurColonne(error)
}

/** Message affiché quand la base de production n'a pas encore la migration 0043. */
export const MESSAGE_MIGRATION_0043 =
  'Réglage disponible après la mise à jour de la base (migration 0043)'

/** Erreur « colonne inconnue » : la base n'est pas à jour, ce n'est pas une panne. */
export class ErreurMigration0043 extends Error {
  constructor() {
    super(MESSAGE_MIGRATION_0043)
    this.name = 'ErreurMigration0043'
  }
}

/**
 * Reconnaît « colonne inconnue » côté Postgres (42703) ou PostgREST (PGRST204,
 * « Could not find the 'x' column ») pour une colonne de la migration 0043.
 * Toute autre erreur est rendue telle quelle : on ne maquille pas une vraie
 * panne en « mise à jour à venir ».
 */
function erreurColonne(err: { code?: string; message?: string }): Error {
  const m = err.message ?? ''
  const colonne0043 = /meteo_actuelle|frais_traversee|bonus_traversee|livraison_min_|tension_|service_fee_haut|premium_reduction_pct|commande_minimum/.test(m)
  const inconnue =
    err.code === '42703' ||
    err.code === 'PGRST204' ||
    /does not exist|could not find|n'existe pas/i.test(m)
  if (colonne0043 && inconnue) return new ErreurMigration0043()
  return new Error(m || 'Enregistrement impossible.')
}

/** Bascule la météo en cours. Le serveur la lit à chaque nouvelle commande. */
export async function enregistrerMeteoActuelle(condition: ConditionMeteo): Promise<void> {
  if (!estConditionMeteo(condition)) throw new Error('Météo inconnue.')
  if (APERCU) return apercuAppliquer({ meteo_actuelle: condition })
  const { error } = await supabase.from('platform_config').update({ meteo_actuelle: condition }).eq('id', 1)
  if (error) throw erreurColonne(error)
}

export interface PalierAEnregistrer extends PalierASaisir {
  /** `null` = palier neuf, à insérer. */
  id: string | null
}

/**
 * Enregistre le barème de livraison COMPLET : met à jour les paliers existants,
 * insère les neufs, supprime ceux retirés.
 *
 * ⚠️ Pas de transaction possible depuis le navigateur : si une étape échoue au
 * milieu, la base garde un barème mixte. D'où l'ordre — modifier, insérer,
 * supprimer en DERNIER — qui laisse au pire un chevauchement passager (le
 * serveur retombe alors sur un palier) plutôt qu'un trou ; et l'écran recharge
 * toujours le barème réel après une erreur, pour ne pas en montrer un faux.
 * La validation est rejouée ici : on n'écrit jamais un barème que l'écran
 * aurait refusé, quel que soit l'appelant.
 */
export async function enregistrerPaliersLivraison(
  paliers: PalierAEnregistrer[],
  idsASupprimer: string[],
  /**
   * Réglages de `platform_config` saisis dans la même carte (0054), seulement
   * s'ils ont changé ; absents = on n'y touche pas.
   */
  extras: { minima?: MinimaASaisir; fraisService?: FraisServiceASaisir } = {},
): Promise<void> {
  const { minima, fraisService } = extras
  const erreurs = [
    ...validerPaliers(paliers, minima).erreurs,
    ...(minima ? validerMinima(minima) : []),
    ...(fraisService ? validerFraisService(fraisService) : []),
  ]
  if (erreurs.length) throw new Error(erreurs[0]!.message)
  if (APERCU) return apercuAppliquer({ paliers, idsASupprimer, ...extras })

  /* Les minima vivent dans `platform_config`, écrite par un ADMINISTRATEUR
     seulement (policy de 0008). Écrits EN PREMIER et seulement s'ils ont
     changé (`minima` absent sinon) : un refus n'aura alors rien modifié. */
  if (minima || fraisService) {
    const { data, error } = await supabase
      .from('platform_config')
      .update({
        ...(minima ? { livraison_min_client: minima.client, livraison_min_livreur: minima.driver } : {}),
        ...(fraisService ? { service_fee: fraisService.bas, service_fee_haut: fraisService.haut } : {}),
      })
      .eq('id', 1)
      .select('id')
    if (error) throw erreurColonne(error)
    if (!data || data.length === 0)
      throw new Error('Rien n’a été enregistré : les prix minimum et les frais de service ne peuvent être modifiés que par un administrateur.')
  }

  const ligne = (p: PalierASaisir) => ({
    radius_min_km: p.minKm,
    radius_max_km: p.maxKm,
    client_fee: p.fraisClient,
    driver_pay: p.partLivreur,
    premium_fee: p.fraisPremium,
  })

  for (const p of paliers) {
    if (p.id === null) continue
    const { error } = await supabase.from('delivery_pricing').update(ligne(p)).eq('id', p.id)
    if (error) throw new Error(error.message)
  }
  const neufs = paliers.filter((p) => p.id === null).map(ligne)
  if (neufs.length) {
    const { error } = await supabase.from('delivery_pricing').insert(neufs)
    if (error) throw new Error(error.message)
  }
  if (idsASupprimer.length) {
    const { error } = await supabase.from('delivery_pricing').delete().in('id', idsASupprimer)
    if (error) throw new Error(error.message)
  }
}

export async function enregistrerBonusMeteo(
  id: string,
  bonusLivreur: number,
  supplementClient: number,
): Promise<void> {
  if (!Number.isFinite(bonusLivreur) || !Number.isFinite(supplementClient) || bonusLivreur < 0 || supplementClient < 0)
    throw new Error('Montant positif attendu.')
  const { error } = await supabase
    .from('weather_bonuses')
    .update({ driver_bonus: bonusLivreur, client_surcharge: supplementClient })
    .eq('id', id)
  if (error) throw error
}

export interface LivreurAssignable {
  id: string
  nom: string
  telephone: string
  vehicule: string
  lat: number | null
  lng: number | null
  /** Dernier relevé GPS (le GPS ne tourne qu'en course, depuis la 0047 : souvent ancien ou absent). */
  positionLe: string | null
  note: number | null
}

/**
 * Les livreurs que l'on peut réellement assigner : ACTIFS (parcours d'intégration
 * terminé, 0045) ET DISPONIBLES (`status = available`).
 *
 * Deux lectures plutôt qu'une jointure : le filtre « actif » se lit dans
 * `driver_onboarding`, table séparée de `drivers`. Un livreur suspendu repasse
 * hors ligne par déclencheur, mais on ne s'y fie pas pour l'affichage — le serveur
 * refuserait de toute façon (`livreur_non_actif`), autant ne pas le proposer.
 */
export async function listerLivreursAssignables(): Promise<LivreurAssignable[]> {
  if (APERCU) {
    return apercuLivreurs()
      .filter((l) => l.etat === 'online')
      .map((l) => ({
        id: l.id, nom: l.nom, telephone: l.telephone,
        vehicule: `${l.typeVehicule ?? ''} ${l.plaque}`.trim(),
        lat: positionConnue(l.lat, l.lng) ? l.lat : null,
        lng: positionConnue(l.lat, l.lng) ? l.lng : null,
        positionLe: l.id === 'apercu-l3' ? new Date(Date.now() - 95 * 60000).toISOString() : new Date().toISOString(),
        note: l.noteMixte,
      }))
  }
  const { data, error } = await supabase
    .from('drivers')
    .select('id,vehicle_type,vehicle_plate,lat,lng,last_location_at,rating,profile:profiles!id(full_name,phone)')
    .eq('status', 'available')
  if (error) throw error
  type Ligne = {
    id: string
    vehicle_type: string | null
    vehicle_plate: string | null
    lat: number | null
    lng: number | null
    last_location_at: string | null
    rating: number | null
    profile: { full_name: string | null; phone: string | null } | null
  }
  const lignes = (data as unknown as Ligne[] | null) ?? []
  if (lignes.length === 0) return []

  const { data: actifs, error: e2 } = await supabase
    .from('driver_onboarding')
    .select('driver_id')
    .eq('status', 'actif')
    .in('driver_id', lignes.map((l) => l.id))
  if (e2) throw e2
  const ids = new Set(((actifs as { driver_id: string }[] | null) ?? []).map((a) => a.driver_id))

  return lignes
    .filter((l) => ids.has(l.id))
    .map((l) => ({
      id: l.id,
      nom: l.profile?.full_name ?? 'Livreur',
      telephone: l.profile?.phone ?? '',
      vehicule: `${l.vehicle_type ?? ''} ${l.vehicle_plate ?? ''}`.trim(),
      lat: l.lat,
      lng: l.lng,
      positionLe: l.last_location_at,
      note: l.rating,
    }))
}

/** Le point de retrait d'un marchand, si sa position a été posée. */
export const pointDuMarchand = (c: { marchandLat: number | null; marchandLng: number | null }): Point | null =>
  positionConnue(c.marchandLat, c.marchandLng) ? { lat: c.marchandLat as number, lng: c.marchandLng as number } : null

/**
 * Affectation AUTOMATIQUE : le premier livreur disponible, comme le fait le
 * bouton « Assigner » de l'original.
 *
 * ⚠️ Une différence assumée avec la console HTML : elle écrivait `orders`
 * DIRECTEMENT (`update({driver_id, status})`), ce qui contourne les contrôles
 * de transition de l'edge function. On passe ici par `set-order-status`, comme
 * tout le reste du cycle de vie et comme l'app livreur — c'est le seul endroit
 * où la règle « une seule porte » était encore violée.
 */
export async function affecterAutomatiquement(
  commandeId: string,
  origine: Point | null = null,
  /** Le client de la commande : jamais candidat, même s'il est livreur (0053). */
  clientId: string | null = null,
): Promise<'ok' | 'aucun_livreur'> {
  /* Même liste que le choix manuel (actifs ET disponibles) : le « premier » ne
     peut plus être un livreur que le serveur refuserait. Et c'est le PLUS PROCHE
     du marchand quand sa position est connue — avant, c'était simplement le
     premier renvoyé par la base. */
  const premier = trierParDistance(sansLeClient(await listerLivreursAssignables(), clientId), origine)[0]
  if (!premier) return 'aucun_livreur'
  await affecterLivreur(commandeId, premier.id)
  return 'ok'
}
